"use client";

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { useLegend, useLegendItem } from "./legend-context";

export interface LegendItemProps {
  /** Container class name */
  className?: string;
  /** Children components (LegendMarker, LegendLabel, LegendValue, LegendProgress) */
  children: ReactNode;
}

export function LegendItem({ className = "", children }: LegendItemProps) {
  const { setHoveredIndex, handleSelect, getLabelRef, hoveredIndex, selectedIndex } =
    useLegend();
  const { index, isHovered, isFaded, isSelected } = useLegendItem();
  const hasHover = useHoverCapable();

  const dimmed =
    hoveredIndex !== null ? isFaded : selectedIndex !== null && !isSelected;

  return (
    <div
      ref={getLabelRef ? getLabelRef(index) : undefined}
      className={cn(
        "cursor-pointer rounded-lg px-2 py-1.5 transition-all duration-150 ease-out",
        (isHovered || isSelected) && "bg-legend-muted",
        dimmed && "opacity-40",
        className,
      )}
      data-hovered={isHovered ? "" : undefined}
      data-selected={isSelected ? "" : undefined}
      onMouseEnter={hasHover ? () => setHoveredIndex(index) : undefined}
      onMouseLeave={hasHover ? () => setHoveredIndex(null) : undefined}
      onClick={() => handleSelect(index)}
    >
      {children}
    </div>
  );
}

LegendItem.displayName = "LegendItem";
